import { useEffect, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  BRAND_COLORS,
  DIFFICULTY_MODE_SPECS,
  RANK_TIERS,
  UI_ASSETS,
  rankForTrophies,
  type MeResponse,
} from '@lk/shared';
import { BrandImg } from '../../components/BrandImg';
import { Button } from '../../components/Button';
import { CoinCounter } from '../../components/CoinCounter';
import { Icon } from '../../components/Icon';
import { Meter, Panel, Stat } from '../../components/Panel';
import { SyncIndicator } from '../../components/SyncIndicator';
import { TopBar } from '../../components/TopBar';
import { LoadingState } from '../../components/States';
import { daysLabel, hryvniaLabel, questionsLabel } from '../../lib/format';
import { walletQuery, playWindowQuery, progressQuery } from '../learner/queries';
import { QuickLoginCard } from '../auth/QuickLoginCard';
import { WithdrawPanel } from './WithdrawPanel';
import { findUnfinishedSession } from '../../offline/sessions';
import type { StoredSession } from '../../offline/db';
import { useUiStore } from '../../store/uiStore';

export function ArenaScreen({ me }: { me: MeResponse }): React.JSX.Element {
  const navigate = useNavigate();
  const learnerId = me.user.learnerId ?? me.learners[0]?.id ?? '';
  const wallet = useQuery({ ...walletQuery(learnerId), enabled: Boolean(learnerId) });
  const progress = useQuery({ ...progressQuery(learnerId, 14), enabled: Boolean(learnerId) });
  const playWindow = useQuery({ ...playWindowQuery(learnerId), enabled: Boolean(learnerId) });
  const [unfinished, setUnfinished] = useState<StoredSession | null>(null);
  const lastMode = useUiStore((s) => s.lastMode);

  useEffect(() => {
    if (!learnerId) return;
    void findUnfinishedSession(learnerId).then((row) => setUnfinished(row ?? null));
  }, [learnerId]);

  /*
   * Перерва — не повідомлення, а стан: якщо вона триває, дитині нема чого
   * робити на арені. Ведемо на екран перерви одразу, ще до того, як вона
   * тицьне «У бій» і отримає 403. Сервер усе одно не пустить, але дитина
   * має бачити відлік, а не помилку.
   */
  const breakUntil = playWindow.data?.breakUntil ?? null;
  useEffect(() => {
    if (!breakUntil) return;
    if (new Date(breakUntil).getTime() <= Date.now()) return;
    void navigate({ to: '/break' });
  }, [breakUntil, navigate]);

  if (!learnerId) return <LoadingState label="Шукаємо твій профіль…" />;

  /**
   * Ранг показуємо від СЕРВЕРНОГО числа зароблених балів за весь час.
   * Клієнт нічого не підсумовує — лише перекладає авторитетне число в ранг.
   */
  const trophies = wallet.data?.totalEarned ?? 0;
  const rank = rankForTrophies(trophies);
  const rankIndex = RANK_TIERS.findIndex((tier) => tier.key === rank.key);
  const nextRank = RANK_TIERS[rankIndex + 1];
  const rankProgress = nextRank
    ? (trophies - rank.minTrophies) / (nextRank.minTrophies - rank.minTrophies)
    : 1;

  const earnedToday = wallet.data?.earnedToday ?? 0;
  /** Стеля заробітку за добу. 0 = без ліміту, і тоді її взагалі не показуємо. */
  const dailyCap = wallet.data?.dailyCap ?? 0;

  /*
   * Денна ціль міряється в ПИТАННЯХ, а не в балах.
   *
   * Раніше смуга з підписом «Денна ціль» рахувала стелю заробітку, чий дефолт
   * нуль означає «без ліміту». Виходило «Сьогодні 12/0», порожня смуга і «ще
   * 0 балів до денної цілі» — тобто ціль, до якої неможливо дійти. Тепер число
   * ціль і число поступу приходять з гаманця: обидва серверні, обидва про
   * питання. Ціль не задана (0) — смуги немає взагалі, бо порожня смуга
   * читається як «нічого не зроблено», а не як «цілі немає».
   */
  const goalItems = wallet.data?.dailyGoalItems ?? 0;
  const answeredToday = wallet.data?.answeredToday ?? 0;
  const goalLeft = Math.max(0, goalItems - answeredToday);
  const goalRatio = goalItems > 0 ? Math.min(1, answeredToday / goalItems) : 0;
  const streakDays = progress.data?.streakDays ?? 0;
  const today = progress.data?.daily.at(-1);

  return (
    <main className="lk-shell lk-safe flex min-h-dvh flex-col gap-4 pb-8">
      {/*
        Ключа до кабінету тут немає: арена — екран дитини, і батько на неї
        просто не потрапляє (маршрут захищений роллю). Кабінет відкривається
        одразу після входу батька.
      */}
      <TopBar avatarKey={me.user.avatarKey} name={me.user.displayName} />

      {/* Ранг і трофеї */}
      <Panel className="relative overflow-hidden p-5">
        <div className="pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full bg-[var(--lk-gold)]/15 blur-2xl" />
        <div className="flex items-center gap-4">
          <BrandImg
            src={rank.asset}
            alt={`Ранг: ${rank.title}`}
            fallbackIcon="trophy"
            colors={[BRAND_COLORS.gold, BRAND_COLORS.goldDeep]}
            shape="rounded"
            className="lk-anim-float h-24 w-24 shrink-0"
            eager
          />
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-black tracking-widest text-white/50 uppercase">Ранг</p>
            <h2 className="lk-title-gold text-3xl">{rank.title}</h2>
            <div className="mt-2 flex items-center gap-2">
              <BrandImg
                src={UI_ASSETS.trophy.path}
                alt="Трофеї"
                fallbackIcon="medal"
                colors={[BRAND_COLORS.gold, BRAND_COLORS.goldDeep]}
                shape="circle"
                className="h-6 w-6"
                decorative
              />
              <CoinCounter value={trophies} className="text-xl" />
            </div>
            <Meter
              className="mt-2"
              value={Math.max(0, Math.min(1, rankProgress))}
              label="Прогрес до наступного рангу"
              from={BRAND_COLORS.cyan}
              to={BRAND_COLORS.gold}
            />
            <p className="mt-1 text-[11px] font-bold text-white/50">
              {nextRank
                ? `${nextRank.minTrophies - trophies} до рангу «${nextRank.title}»`
                : 'Максимальний ранг — ти легенда!'}
            </p>
          </div>
        </div>
      </Panel>

      {/* Гаманець */}
      <Panel className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-black tracking-widest text-white/50 uppercase">
              Гаманець
            </p>
            <div className="mt-1 flex items-baseline gap-2">
              <BrandImg
                src={UI_ASSETS.coinStack.path}
                alt="Монети"
                fallbackIcon="coin"
                colors={[BRAND_COLORS.gold, BRAND_COLORS.goldDeep]}
                shape="circle"
                className="h-9 w-9 self-center"
                decorative
              />
              <CoinCounter
                value={wallet.data?.balance ?? 0}
                className="text-4xl"
                suffix={<span className="text-lg text-white/60">грн</span>}
              />
            </div>
            <p className="mt-1 text-xs font-bold text-white/55">
              зароблено за весь час: {hryvniaLabel(wallet.data?.totalEarned ?? 0)} · виплачено:{' '}
              {hryvniaLabel(wallet.data?.totalPaidOut ?? 0)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[11px] font-black tracking-widest text-white/50 uppercase">Сьогодні</p>
            <p className="tabular text-2xl font-black text-[var(--lk-gold)]" data-testid="earned-today">
              {earnedToday}
              {/* Стеля — це стеля, а не ціль: показуємо її лише коли вона є. */}
              {dailyCap > 0 ? <span className="text-sm text-white/45">/{dailyCap}</span> : null}
            </p>
            {dailyCap > 0 ? (
              <p className="text-[10px] font-bold text-white/40">стеля за добу</p>
            ) : null}
          </div>
        </div>
        {goalItems > 0 ? (
          <div data-testid="daily-goal">
            <Meter
              className="mt-3"
              value={goalRatio}
              label="Денна ціль"
              from={BRAND_COLORS.gold}
              to={BRAND_COLORS.green}
            />
            <p className="mt-1 text-[11px] font-bold text-white/50">
              {goalLeft === 0
                ? 'Денна ціль виконана. Далі — вже заради рангу.'
                : `${answeredToday} з ${goalItems} — ще ${questionsLabel(goalLeft)} до денної цілі`}
            </p>
          </div>
        ) : null}
      </Panel>

      <WithdrawPanel learnerId={learnerId} wallet={wallet.data} />

      {/* Показники дня */}
      <div className="grid grid-cols-3 gap-3">
        <Stat
          label="Серія"
          icon={<Icon name="streak" size={14} />}
          value={streakDays}
          hint={daysLabel(streakDays)}
          tone={BRAND_COLORS.gold}
        />
        <Stat
          label="Сьогодні"
          icon={<Icon name="checklist" size={14} />}
          value={today?.attempts ?? 0}
          hint={questionsLabel(today?.attempts ?? 0)}
          tone={BRAND_COLORS.cyan}
        />
        <Stat
          label="Влучність"
          icon={<Icon name="target" size={14} />}
          value={
            today && today.attempts > 0
              ? `${Math.round((today.correct / today.attempts) * 100)}%`
              : '—'
          }
          hint="за сьогодні"
          tone={BRAND_COLORS.green}
        />
      </div>

      {unfinished ? (
        <Panel tight className="border-[var(--lk-cyan)]/60 p-4">
          <p className="text-sm font-black">Є незавершена сесія</p>
          <p className="mt-1 text-xs font-semibold text-white/60">
            Зупинка на питанні {unfinished.cursor + 1} з {unfinished.payload.items.length}.
            Прогрес на місці.
          </p>
          <Button
            tone="cyan"
            block
            className="mt-3"
            onClick={() =>
              void navigate({
                to: '/play/$sessionId',
                params: { sessionId: unfinished.sessionId },
              })
            }
          >
            Продовжити
          </Button>
        </Panel>
      ) : null}

      {/* Головна дія */}
      <Button
        tone="gold"
        size="xl"
        block
        className="mt-1"
        onClick={() => void navigate({ to: '/modes' })}
        data-testid="play-button"
      >
        <Icon name="play" size={28} />
        Грати
      </Button>

      <div className="grid grid-cols-3 gap-2">
        {Object.values(DIFFICULTY_MODE_SPECS).map((spec) => (
          <Link
            key={spec.id}
            to="/modes"
            search={{ mode: spec.id }}
            className="lk-panel-tight px-2 py-3 text-center text-xs font-black"
            style={spec.id === lastMode ? { borderColor: BRAND_COLORS.gold } : undefined}
          >
            {spec.title}
          </Link>
        ))}
      </div>

      {/*
        Швидкий вхід — унизу, під головною дією: це налаштування, а не привід
        відволікати від кнопки «Грати».
      */}
      <QuickLoginCard />

      <SyncIndicator variant="full" className="mt-2" />
    </main>
  );
}
